import { modelUrl } from "@/lib/ai/detection/model-url"
import * as tf from "./tf"
import type { RfDetrPersonBox } from "./rfdetr-person-segmentation"
import type { Rect } from "./face-detection"
import { clamp } from "@/utils/math"
import { LazyGraphModel } from "./lazy-graph-model"
import PQueue from "p-queue"

export const SCHP_HUMAN_PARSER_INPUT_SIZE = 473
export const SCHP_HUMAN_PARSER_MODEL_PATH = modelUrl("schp-cihp-global-tfjs-int8")

export type SchpHumanParsingPart = "clothing" | "bodySkin" | "facialSkin" | "hair"

export interface SchpHumanParsingResult {
	classMask: Uint8Array
	width: number
	height: number
	crop: Rect
}

interface PixelRect {
	x: number
	y: number
	width: number
	height: number
}

const SCHP_CIHP_CLASS_NAMES = [
	"Background",
	"Hat",
	"Hair",
	"Glove",
	"Sunglasses",
	"Upper-clothes",
	"Dress",
	"Coat",
	"Socks",
	"Pants",
	"Torso-skin",
	"Scarf",
	"Skirt",
	"Face",
	"Left-arm",
	"Right-arm",
	"Left-leg",
	"Right-leg",
	"Left-shoe",
	"Right-shoe",
] as const

const SCHP_CLASS_IDS = {
	background: 0,
	hat: 1,
	hair: 2,
	glove: 3,
	sunglasses: 4,
	upperClothes: 5,
	dress: 6,
	coat: 7,
	socks: 8,
	pants: 9,
	torsoSkin: 10,
	scarf: 11,
	skirt: 12,
	face: 13,
	leftArm: 14,
	rightArm: 15,
	leftLeg: 16,
	rightLeg: 17,
	leftShoe: 18,
	rightShoe: 19,
} as const

const SCHP_PART_CLASSES: Record<SchpHumanParsingPart, number[]> = {
	clothing: [
		SCHP_CLASS_IDS.hat,
		SCHP_CLASS_IDS.glove,
		SCHP_CLASS_IDS.sunglasses,
		SCHP_CLASS_IDS.upperClothes,
		SCHP_CLASS_IDS.dress,
		SCHP_CLASS_IDS.coat,
		SCHP_CLASS_IDS.socks,
		SCHP_CLASS_IDS.pants,
		SCHP_CLASS_IDS.scarf,
		SCHP_CLASS_IDS.skirt,
		SCHP_CLASS_IDS.leftShoe,
		SCHP_CLASS_IDS.rightShoe,
	],
	bodySkin: [
		SCHP_CLASS_IDS.torsoSkin,
		SCHP_CLASS_IDS.leftArm,
		SCHP_CLASS_IDS.rightArm,
		SCHP_CLASS_IDS.leftLeg,
		SCHP_CLASS_IDS.rightLeg,
	],
	facialSkin: [SCHP_CLASS_IDS.face],
	hair: [SCHP_CLASS_IDS.hair],
}

const personCropPadding = 0.1
const schpBgrMean = [0.406, 0.456, 0.485]
const schpBgrStd = [0.225, 0.224, 0.229]

export class SchpHumanParser {
	private model = new LazyGraphModel(SCHP_HUMAN_PARSER_MODEL_PATH)
	private inferenceQueue = new PQueue({ concurrency: 1 })

	public async loadModel(onProgress?: (progress: number) => void) {
		await this.model.load(onProgress)
	}

	public cleanup() {
		this.model.cleanup()
	}

	public process(
		input: ImageBitmap,
		personBox: RfDetrPersonBox,
	): Promise<SchpHumanParsingResult> {
		return this.inferenceQueue.add(() => this.processInternal(input, personBox))
	}

	private async processInternal(
		input: ImageBitmap,
		personBox: RfDetrPersonBox,
	): Promise<SchpHumanParsingResult> {
		const model = await this.model.load()

		const crop = createSchpPersonCrop(personBox, input.width, input.height)
		const pixelCrop = toPixelCrop(crop, input.width, input.height)
		const { imageData, contentRect } = cropImageBitmapToModelInput(input, pixelCrop)
		let inputTensor: tf.Tensor | undefined
		let outputs: tf.Tensor[] = []
		let classMaskTensor: tf.Tensor | undefined

		try {
			inputTensor = preprocessSchpPixels(imageData.data, imageData.width, imageData.height)

			const rawOutputs = model.execute({ input: inputTensor })
			outputs = Array.isArray(rawOutputs) ? rawOutputs : [rawOutputs]
			const logits = selectSchpHumanParserLogits(outputs)
			classMaskTensor = tf.tidy(() => tf.squeeze(tf.argMax(logits, 3), [0]))
			const modelClassMask = new Uint8Array(await classMaskTensor.data())
			const classMask = resampleClassMaskToCrop({
				classMask: modelClassMask,
				sourceWidth: SCHP_HUMAN_PARSER_INPUT_SIZE,
				sourceHeight: SCHP_HUMAN_PARSER_INPUT_SIZE,
				contentRect,
				cropWidth: pixelCrop.width,
				cropHeight: pixelCrop.height,
			})

			return {
				classMask,
				width: pixelCrop.width,
				height: pixelCrop.height,
				crop,
			}
		} finally {
			classMaskTensor?.dispose()
			outputs.forEach((tensor) => tensor.dispose())
			inputTensor?.dispose()
		}
	}
}

export function createSchpPersonCrop(
	box: RfDetrPersonBox,
	imageWidth: number,
	imageHeight: number,
): Rect {
	const padding = Math.max(box.width, box.height) * personCropPadding
	const xMin = clamp(box.xMin - padding, 0, imageWidth)
	const yMin = clamp(box.yMin - padding, 0, imageHeight)
	const xMax = clamp(box.xMin + box.width + padding, 0, imageWidth)
	const yMax = clamp(box.yMin + box.height + padding, 0, imageHeight)

	return {
		x: xMin / imageWidth,
		y: yMin / imageHeight,
		width: Math.max(0, xMax - xMin) / imageWidth,
		height: Math.max(0, yMax - yMin) / imageHeight,
	}
}

export function preprocessSchpPixels(data: Uint8ClampedArray, width: number, height: number) {
	return tf.tidy(() => {
		const rgba = tf.tensor3d(data as unknown as Uint8Array, [height, width, 4], "int32")
		const rgb = rgba.slice([0, 0, 0], [height, width, 3]).toFloat().div(255)
		const bgr = tf.reverse(rgb, 2)
		const mean = tf.tensor1d(schpBgrMean)
		const std = tf.tensor1d(schpBgrStd)
		return bgr.sub(mean).div(std).expandDims(0)
	})
}

export function createSchpPartMask(classMask: Uint8Array, part: SchpHumanParsingPart) {
	const classIds = new Set(SCHP_PART_CLASSES[part])
	const mask = new Float32Array(classMask.length)
	for (let index = 0; index < classMask.length; index += 1) {
		mask[index] = classIds.has(classMask[index] ?? SCHP_CLASS_IDS.background) ? 1 : 0
	}
	return mask
}

export function projectSchpPartMask(options: {
	partMask: Float32Array
	partWidth: number
	partHeight: number
	crop: Rect
	personMask: Float32Array
	outputWidth: number
	outputHeight: number
}) {
	const result = new Float32Array(options.outputWidth * options.outputHeight)
	const startX = Math.max(0, Math.floor(options.crop.x * options.outputWidth))
	const startY = Math.max(0, Math.floor(options.crop.y * options.outputHeight))
	const endX = Math.min(
		options.outputWidth,
		Math.ceil((options.crop.x + options.crop.width) * options.outputWidth),
	)
	const endY = Math.min(
		options.outputHeight,
		Math.ceil((options.crop.y + options.crop.height) * options.outputHeight),
	)

	for (let y = startY; y < endY; y += 1) {
		const sourceY = clamp(
			Math.floor(
				(((y + 0.5) / options.outputHeight - options.crop.y) / options.crop.height) *
					options.partHeight,
			),
			0,
			options.partHeight - 1,
		)
		for (let x = startX; x < endX; x += 1) {
			const outputIndex = y * options.outputWidth + x
			if ((options.personMask[outputIndex] ?? 0) <= 0.5) {
				continue
			}

			const sourceX = clamp(
				Math.floor(
					(((x + 0.5) / options.outputWidth - options.crop.x) / options.crop.width) *
						options.partWidth,
				),
				0,
				options.partWidth - 1,
			)
			result[outputIndex] = options.partMask[sourceY * options.partWidth + sourceX] ?? 0
		}
	}
	return result
}

export function selectSchpHumanParserLogits(outputs: tf.Tensor[]) {
	const identity = outputs.find(
		(tensor) =>
			"name" in tensor && tensor.name === "Identity" && isSchpHumanParserLogits(tensor),
	)
	if (identity) {
		return identity
	}

	const logits = outputs.find(isSchpHumanParserLogits)
	if (!logits) {
		throw new Error("SCHP human parser logits output was not found")
	}
	return logits
}

function isSchpHumanParserLogits(tensor: tf.Tensor) {
	return (
		tensor.shape.length === 4 &&
		tensor.shape[1] === SCHP_HUMAN_PARSER_INPUT_SIZE &&
		tensor.shape[2] === SCHP_HUMAN_PARSER_INPUT_SIZE &&
		tensor.shape[3] === SCHP_CIHP_CLASS_NAMES.length
	)
}

function cropImageBitmapToModelInput(input: ImageBitmap, crop: PixelRect) {
	const canvas = new OffscreenCanvas(SCHP_HUMAN_PARSER_INPUT_SIZE, SCHP_HUMAN_PARSER_INPUT_SIZE)
	const context = canvas.getContext("2d")
	if (!context) {
		throw new Error("Could not create SCHP crop canvas context")
	}

	context.fillStyle = "black"
	context.fillRect(0, 0, SCHP_HUMAN_PARSER_INPUT_SIZE, SCHP_HUMAN_PARSER_INPUT_SIZE)

	const scale = Math.min(
		SCHP_HUMAN_PARSER_INPUT_SIZE / crop.width,
		SCHP_HUMAN_PARSER_INPUT_SIZE / crop.height,
	)
	const width = Math.max(1, Math.round(crop.width * scale))
	const height = Math.max(1, Math.round(crop.height * scale))
	const x = Math.floor((SCHP_HUMAN_PARSER_INPUT_SIZE - width) / 2)
	const y = Math.floor((SCHP_HUMAN_PARSER_INPUT_SIZE - height) / 2)

	context.drawImage(input, crop.x, crop.y, crop.width, crop.height, x, y, width, height)

	return {
		imageData: context.getImageData(
			0,
			0,
			SCHP_HUMAN_PARSER_INPUT_SIZE,
			SCHP_HUMAN_PARSER_INPUT_SIZE,
		),
		contentRect: { x, y, width, height },
	}
}

function resampleClassMaskToCrop(options: {
	classMask: Uint8Array
	sourceWidth: number
	sourceHeight: number
	contentRect: PixelRect
	cropWidth: number
	cropHeight: number
}) {
	const classMask = new Uint8Array(options.cropWidth * options.cropHeight)
	for (let y = 0; y < options.cropHeight; y += 1) {
		const sourceY = clamp(
			Math.floor(
				options.contentRect.y +
					((y + 0.5) / options.cropHeight) * options.contentRect.height,
			),
			0,
			options.sourceHeight - 1,
		)
		for (let x = 0; x < options.cropWidth; x += 1) {
			const sourceX = clamp(
				Math.floor(
					options.contentRect.x +
						((x + 0.5) / options.cropWidth) * options.contentRect.width,
				),
				0,
				options.sourceWidth - 1,
			)
			classMask[y * options.cropWidth + x] =
				options.classMask[sourceY * options.sourceWidth + sourceX] ??
				SCHP_CLASS_IDS.background
		}
	}
	return classMask
}

function toPixelCrop(crop: Rect, imageWidth: number, imageHeight: number): PixelRect {
	const x = clamp(Math.round(crop.x * imageWidth), 0, imageWidth - 1)
	const y = clamp(Math.round(crop.y * imageHeight), 0, imageHeight - 1)
	const width = clamp(Math.round(crop.width * imageWidth), 1, imageWidth - x)
	const height = clamp(Math.round(crop.height * imageHeight), 1, imageHeight - y)
	return { x, y, width, height }
}
